/* "Start where you are": reading memory for the home page's levels section
   ([aria-labelledby="levels-heading"]). Recovered 2026-10-13 from four blocks
   hand-appended to the dev server's dist/app.js (branch
   origin/dev-server-snapshot-2026-10-07); they had no source file. They are kept
   in their original order because each later block patches the earlier ones.
   State is per-visitor localStorage. */

/* ---------------------------------------------------------------------------
   Reading memory for "Start where you are" — appended by hand, 2026-09-30.

   The section now responds to what THIS visitor has actually read: pages they
   finished are marked done, and the first one they have not finished is
   promoted to the front as the obvious next step.

   IT NEVER LEAVES THE BROWSER. Everything below is localStorage on this origin.
   Nothing is sent anywhere, no id is minted, and the platform's own
   dxpmessages-user cookie is deliberately NOT read - that cookie would let a
   visitor's pages be stitched into a trail server-side, which is exactly the
   record we decided not to build. Clear it from the console with
   localStorage.removeItem('hub:read').

   WHAT COUNTS AS READ: scrolled past 75% AND at least 30s on the page. Either
   alone is a bad signal - a fast scroll to the bottom is not reading, and a
   parked tab is not either. Short pages that never scroll count on dwell alone,
   since 75% of a page shorter than the viewport is unreachable.

   Capped at 200 entries, oldest evicted, so the store cannot grow without
   bound on a documentation site with several hundred pages.
   ------------------------------------------------------------------------ */
export function markReadOnScroll() {
	var KEY = 'hub:read';
	var NEED_SCROLL = 0.75, NEED_MS = 30000, CAP = 200;

	function load() {
		try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
	}
	function save(map) {
		try {
			var keys = Object.keys(map);
			if (keys.length > CAP) {
				keys.sort(function (a, b) { return map[a] - map[b]; })
					.slice(0, keys.length - CAP)
					.forEach(function (k) { delete map[k]; });
			}
			localStorage.setItem(KEY, JSON.stringify(map));
		} catch (e) { /* private mode, quota - the feature is optional */ }
	}

	/* ---- 1. record this page ---- */
	function watch() {
		var path = location.pathname;
		if (path === '/' || load()[path]) { return; }
		var started = Date.now(), deep = false, done = false;

		function depth() {
			var h = document.documentElement;
			var scrollable = h.scrollHeight - h.clientHeight;
			if (scrollable < 80) { return 1; }            /* short page: nothing to scroll */
			return (h.scrollTop || document.body.scrollTop) / scrollable;
		}
		function check() {
			if (done) { return; }
			if (!deep && depth() >= NEED_SCROLL) { deep = true; }
			if (deep && Date.now() - started >= NEED_MS) {
				done = true;
				var map = load(); map[path] = Date.now(); save(map);
				window.removeEventListener('scroll', check);
			}
		}
		window.addEventListener('scroll', check, { passive: true });
		/* Cleared once the page qualifies. The first version left this running for
		   the life of the tab, firing a no-op every 5s forever. */
		var timer = setInterval(function () { check(); if (done) { clearInterval(timer); } }, 5000);
	}

	/* ---- 2. reflect it on the homepage section ---- */
	function paint() {
		var sec = document.querySelector('[aria-labelledby="levels-heading"]');
		if (!sec) { return; }
		var read = load();
		var rows = Array.prototype.slice.call(sec.querySelectorAll('.article-row'));
		if (!rows.length) { return; }

		var next = null;
		rows.forEach(function (row) {
			var href = row.getAttribute('href');
			if (href && read[href]) { row.classList.add('is-read'); }
			else if (!next) { next = row; }
		});

		/* Only mark a next step once something has actually been read - on a
		   first visit the section should look untouched, not pre-empted. */
		/* counts a click as engagement too - requiring a finished read meant a
		   visitor who had clicked several cards still saw no next-step marker */
		var any = rows.some(function (r) {
			return r.classList.contains('is-read') || r.classList.contains('is-open');
		});
		if (any && next) {
			next.classList.add('is-next');
			sec.classList.add('has-progress');
		}
	}

	function init() { watch(); paint(); }
	if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
}


/* ---------------------------------------------------------------------------
   "Start where you are" — click memory, appended 2026-09-30.

   The block above records a page as READ on scroll depth plus dwell. That
   misses the most direct signal there is: opening the thing. A visitor who
   clicks a card and bounces straight back has still told us which step they
   are on, and until now the section forgot it instantly.

   So a click is recorded separately and immediately. Two states, because they
   mean different things and collapsing them would overstate what happened:

     hub:open   you opened it          - shown as a hollow marker
     hub:read   you got through it     - shown as a tick, set by the block above

   Recorded on pointerdown rather than click: a middle-click or Cmd-click opens
   a new tab and never fires a plain click, and those are exactly the readers
   queueing several pages at once.

   Same storage rules as above: localStorage on this origin, nothing sent
   anywhere, no id minted, the platform session cookie untouched. Clear with
   localStorage.removeItem('hub:open').
   ------------------------------------------------------------------------ */
export function markReadOnClick() {
	var KEY = 'hub:open';
	function load() { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; } }
	function save(m) { try { localStorage.setItem(KEY, JSON.stringify(m)); } catch (e) {} }

	var SEC = '[aria-labelledby="levels-heading"]';

	document.addEventListener('pointerdown', function (e) {
		var row = e.target && e.target.closest ? e.target.closest(SEC + ' .article-row') : null;
		if (!row) { return; }
		var href = row.getAttribute('href');
		if (!href) { return; }
		var m = load();
		if (!m[href]) { m[href] = Date.now(); save(m); }
		row.classList.add('is-open');
	});

	function paintOpens() {
		var sec = document.querySelector(SEC);
		if (!sec) { return; }
		var opened = load();
		Array.prototype.forEach.call(sec.querySelectorAll('.article-row'), function (row) {
			var href = row.getAttribute('href');
			/* read beats open - never show both markers on one row */
			if (href && opened[href] && !row.classList.contains('is-read')) {
				row.classList.add('is-open');
			}
		});
	}
	if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', paintOpens); } else { paintOpens(); }
}


/* ---------------------------------------------------------------------------
   "Start where you are": the ORDER follows the reader, 2026-09-30.

   Until now this section was dynamic only in appearance - clicking or finishing
   a page recoloured its card, but the eight cards stayed in their CMS order, so
   the top of the section was "Start here" whether or not you had read it.

   Now the order is rebuilt on every load from what you have actually done.
   Rewritten 2026-10-01 from three fixed groups to a score - see the scoring
   comment inside run() for the weights and the reasoning behind each one.

   The short version: every card gets a number built from how far you scrolled
   on it, how recently you touched it, and which level you are working at. The
   highest number goes first and carries .is-next. Finished pages sink to the
   bottom without disappearing - still there to re-open, just out of the way.

   WHY A SCORE AND NOT GROUPS. Groups answer "what state is this card in".
   The section exists to answer "which one should I open now", and those are
   different questions: four cards all in progress came back in CMS order, so
   the page you were 80% through could sit below one you bounced off weeks ago.

   THE LADDER IS STILL RESPECTED. Level is a term in the score, not a sort key
   that gets overridden - a lower rung wins every tie, and untouched cards are
   offered at the level you are actually working at rather than from the start.

   A FIRST-TIME READER SEES NO CHANGE. With nothing read and nothing clicked,
   every card is in group 2 and the original order is returned untouched - the
   section must not look rearranged to someone who has done nothing yet.

   Reads the same localStorage as the painters above; nothing is sent anywhere.
   Runs last because it depends on the classes they add.

   The colour stripes are nth-child based, so they are reapplied after the move
   by re-stamping a level class from each card's own label - otherwise a card
   would take the colour of whatever position it landed in.
   ------------------------------------------------------------------------ */
export function orderByReader() {
	function run() {
		var sec = document.querySelector('[aria-labelledby="levels-heading"]');
		if (!sec) { return; }
		var grid = sec.querySelector('.articles-grid');
		if (!grid) { return; }

		var rows = Array.prototype.slice.call(grid.querySelectorAll('.article-row'));
		if (rows.length < 2) { return; }

		/* stamp each card with its own level, taken from its visible label, so the
		   colour survives reordering instead of following grid position */
		/* Level is taken from the WORDS, not a leading digit. It used to read the
		   first character, which tied the colour scheme to the label happening to
		   start "2 · ". Dropping the numbers from the copy would then have silently
		   un-coloured every card, because nothing else carries the level. */
		var LEVEL_WORDS = [
			['new to the platform', '1'],
			['doing the work', '2'],
			['going deeper', '3'],
			['specialist areas', '4']
		];
		rows.forEach(function (row, i) {
			var path = row.querySelector('.ar-path');
			var txt = path && path.textContent ? path.textContent.toLowerCase() : '';
			var lvl = '';
			for (var k = 0; k < LEVEL_WORDS.length; k++) {
				if (txt.indexOf(LEVEL_WORDS[k][0]) >= 0) { lvl = LEVEL_WORDS[k][1]; break; }
			}
			/* still honour a leading digit if the label keeps one */
			if (!lvl) {
				var c = txt.trim().charAt(0);
				if (c >= '1' && c <= '4') { lvl = c; }
			}
			if (lvl) { row.setAttribute('data-level', lvl); }
			row.setAttribute('data-origin', i);          /* stable tiebreak */
		});

		/* ------------------------------------------------------------------
		   WHAT TO READ NEXT IS NOW SCORED, 2026-10-01.

		   The old version sorted into four fixed buckets: in-progress, next,
		   unread, finished. That answers "what state is this card in" but not
		   the question the section exists to answer, which is "of the things I
		   have not finished, which one should I open now". Four cards all
		   marked in-progress came back in CMS order, so the one you were 80%
		   through sat below one you bounced off three weeks ago.

		   Every card now gets a number, and the number is built from what this
		   reader actually did - how far they scrolled (hub:depth), what they
		   clicked (hub:open), what they finished (hub:read).

		   THE FIVE THINGS IT WEIGHS, and why each one is in there:

		     1  HOW CLOSE TO DONE          100 + 120 x depth
		        The strongest signal there is. Finishing a page you are 80%
		        through costs a few minutes; starting a new one costs an hour.
		        Anything touched outranks anything untouched, and among touched
		        cards the furthest-read comes first.

		     2  IS THE THREAD STILL WARM   up to +40, gone after 7 days
		        What you were reading yesterday is what you meant to come back
		        to. What you opened in July is not.

		     3  DID YOU BOUNCE             -90
		        Opened over two weeks ago and never got past 15%. That is not an
		        unfinished page, it is a page you looked at and rejected, and
		        putting it at the top every visit is how a list loses trust.

		     4  LEVEL FIT (untouched only) 60 - 18 x distance, +10 for the next rung
		        For cards with no history the best guess is the ladder. "Focus"
		        is the highest level this reader has actually worked at, so
		        somebody deep in level 3 is offered level 3 and 4 material, not
		        sent back to "Start here". A beginner stays at focus 1 and gets
		        the beginner path, which is the same behaviour as before.

		     5  LADDER TIEBREAK            -2 x level
		        All else equal, the earlier rung first. The levels are a
		        sequence and sorting must not scramble it.

		   Finished cards score -1000 and sink, keeping CMS order among
		   themselves. They are still there to re-open, just out of the way.

		   The top-scoring unfinished card gets .is-next, which is what draws
		   the pill and the coloured border. It is assigned HERE and nowhere
		   else: the reader-memory block above also tries to set it, but it runs
		   before clicks are painted, so it could only ever fire for somebody
		   who had finished a page. Any .is-next it left behind is cleared
		   first, so exactly one card carries it.
		   ------------------------------------------------------------------ */
		function store(k) {
			try { return JSON.parse(localStorage.getItem(k)) || {}; } catch (e) { return {}; }
		}
		function norm(u) {
			if (!u) { return ''; }
			u = u.split('#')[0].split('?')[0];
			return u.length > 1 && u.charAt(u.length - 1) === '/' ? u.slice(0, -1) : u;
		}
		var DEPTH = store('hub:depth'), OPEN = store('hub:open');
		var DAY = 86400000, NOW = Date.now();

		function depthOf(r) {
			var e = DEPTH[norm(r.getAttribute('href'))];
			return e && e[0] ? e[0] : 0;
		}
		function touchedAt(r) {
			var h = norm(r.getAttribute('href'));
			var e = DEPTH[h];
			return Math.max((e && e[1]) || 0, OPEN[h] || 0);
		}
		function levelOf(r) { return (+r.getAttribute('data-level')) || 1; }

		var touched = rows.some(function (r) {
			return r.classList.contains('is-read') || r.classList.contains('is-open')
				|| depthOf(r) > 0;
		});
		if (!touched) { return; }                        /* first visit: leave alone */

		/* Focus = the highest rung this reader has actually worked at, not just
		   glanced at. A third of a page is the bar; below that they bounced and
		   it says nothing about where they are. */
		var focus = 1;
		rows.forEach(function (r) {
			if (r.classList.contains('is-read') || depthOf(r) >= 0.3) {
				focus = Math.max(focus, levelOf(r));
			}
		});

		function score(r) {
			var lvl = levelOf(r);
			if (r.classList.contains('is-read')) { return -1000 - lvl; }

			var d = depthOf(r);
			var seen = d > 0 || r.classList.contains('is-open') || !!OPEN[norm(r.getAttribute('href'))];
			var s = 0;

			if (seen) {
				s += 100 + Math.round(d * 120);                        /* 1 */
				var age = touchedAt(r) ? (NOW - touchedAt(r)) / DAY : 999;
				s += Math.round(40 * Math.max(0, 1 - age / 7));        /* 2 */
				if (d < 0.15 && age > 14) { s -= 90; }                 /* 3 */
			} else {
				s += 60 - 18 * Math.abs(lvl - focus);                  /* 4 */
				if (lvl === focus + 1) { s += 10; }
			}
			return s - lvl * 2;                                        /* 5 */
		}

		var sorted = rows.slice().sort(function (a, b) {
			var d = score(b) - score(a);                 /* highest score first */
			if (d !== 0) { return d; }
			return (+a.getAttribute('data-origin')) - (+b.getAttribute('data-origin'));
		});

		/* exactly one next step, and it is the top of the list */
		rows.forEach(function (r) { r.classList.remove('is-next'); });
		for (var n = 0; n < sorted.length; n++) {
			if (!sorted[n].classList.contains('is-read')) {
				sorted[n].classList.add('is-next');
				sec.classList.add('has-progress');
				break;
			}
		}

		var changed = sorted.some(function (r, i) { return r !== rows[i]; });
		if (!changed) { return; }

		/* Empty the grid before re-appending. Appending only the <a> elements left
		   the whitespace text nodes that sat between them stranded at the top, so
		   the row of cards gained a block of blank space above it that grew with
		   the number of cards moved. Removing every child first takes those text
		   nodes with it. */
		while (grid.firstChild) { grid.removeChild(grid.firstChild); }
		var frag = document.createDocumentFragment();
		sorted.forEach(function (r) { frag.appendChild(r); });
		grid.appendChild(frag);
		sec.setAttribute('data-reordered', 'true');
	}
	if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', run); } else { run(); }
}


/* ---------------------------------------------------------------------------
   "Start where you are": the ring becomes a real progress ring, 2026-10-01.

   WHAT WAS WRONG. The ring was never a progress indicator. Its arc came from
   --fill, which CSS set from data-level, so it reported DIFFICULTY: 25% for a
   beginner page, 100% for a specialist one. Nothing ever changed it. "Commerce"
   showed a complete circle to someone who had never opened it, and "Start here"
   stayed a quarter full after you had finished it. A control that looks like a
   progress ring and reports something else is worse than no control, because a
   reader trusts it before they question it.

   WHAT IT DOES NOW. The arc is the furthest THIS reader has scrolled on that
   page. The level is still in the colour, so nothing is lost:

     colour   how hard the page is   (data-level, from the CMS)
     arc      how far you got        (hub:depth, from your scrolling)

   HOW THE NUMBER IS COLLECTED. The reader-memory block above already computes
   scroll depth on every page but throws it away once it crosses the 75% read
   threshold. This keeps the running maximum instead. The maximum, not the
   current position - scrolling back up is not losing progress.

   TWO DELIBERATE DISTORTIONS, both so the ring cannot tell a lie:

     a full circle is reserved for finished. An unread page is capped at 92%, so
     there is always a visible gap until the tick appears. Otherwise a page
     short enough to fit the viewport would show a complete ring the instant it
     loaded, which claims a finish that never happened.

     an opened page is floored at 8%. Clicking in and leaving immediately is
     real progress and should be visible; a 1% arc renders as nothing at all.

   WRITES ARE THROTTLED. localStorage.setItem is synchronous and this runs on a
   passive scroll handler, so it saves only when the maximum advances by 5
   points, plus once on pagehide to catch the rest. Writing on every scroll
   event would put a disk write in the scroll path.

   Same rules as the blocks above: localStorage on this origin, nothing sent
   anywhere, no id minted, capped at 200 pages with the oldest evicted. Clear it
   with localStorage.removeItem('hub:depth').
   ------------------------------------------------------------------------ */
export function progressRing() {
	var KEY = 'hub:depth';
	var CAP = 200, STEP = 0.05, FLOOR = 0.08, CEIL = 0.92;
	var SEC = '[aria-labelledby="levels-heading"]';

	function load() {
		try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
	}
	function save(map) {
		try {
			var keys = Object.keys(map);
			if (keys.length > CAP) {
				keys.sort(function (a, b) { return map[a][1] - map[b][1]; })
					.slice(0, keys.length - CAP)
					.forEach(function (k) { delete map[k]; });
			}
			localStorage.setItem(KEY, JSON.stringify(map));
		} catch (e) { /* private mode, quota - the feature is optional */ }
	}
	/* one spelling of a path, so /x and /x/ are not two different cards */
	function norm(p) {
		if (!p) { return ''; }
		p = p.split('#')[0].split('?')[0];
		return p.length > 1 && p.charAt(p.length - 1) === '/' ? p.slice(0, -1) : p;
	}

	/* ---- 1. record how far this page was read ---- */
	function record() {
		var path = norm(location.pathname);
		if (!path || path === '/') { return; }

		function depth() {
			var h = document.documentElement;
			var scrollable = h.scrollHeight - h.clientHeight;
			/* nothing to scroll: the whole page is already on screen */
			if (scrollable < 80) { return 1; }
			var y = h.scrollTop || document.body.scrollTop || 0;
			var d = y / scrollable;
			return d < 0 ? 0 : (d > 1 ? 1 : d);
		}

		var map = load();
		var best = (map[path] && map[path][0]) || 0;
		var saved = best;

		function flush() {
			if (best <= saved) { return; }
			var m = load();                      /* re-read: another tab may have written */
			if (!m[path] || m[path][0] < best) { m[path] = [best, Date.now()]; save(m); }
			saved = best;
		}
		function tick() {
			var d = depth();
			if (d > best) { best = d; }
			if (best - saved >= STEP) { flush(); }
		}

		tick();
		flush();                                  /* the arrival itself is progress */
		window.addEventListener('scroll', tick, { passive: true });
		window.addEventListener('pagehide', flush);
		document.addEventListener('visibilitychange', function () {
			if (document.visibilityState === 'hidden') { flush(); }
		});
	}

	/* ---- 2. draw it on the section ---- */
	function store(k) {
		try { return JSON.parse(localStorage.getItem(k)) || {}; } catch (e) { return {}; }
	}
	/* the level words come from the DOM, so they are escaped before going back
	   into innerHTML - the only untrusted thing on this line */
	function esc(t) {
		return t.replace(/&/g, '&amp;').replace(/</g, '&lt;')
			.replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	/* ------------------------------------------------------------------
	   THE HEADING IS NOW A STATEMENT ABOUT THIS READER, 2026-10-01.

	   "Where to start" was right exactly once - on a first visit. After that
	   the section is not a starting point, it is a ranked shortlist of what
	   is worth opening next, and a heading that never changes was telling
	   three quarters of its readers the wrong thing about the list below it.

	   The CMS copy is still what renders with no JavaScript, and it is still
	   the only version a crawler would see - which costs nothing here, since
	   robots.txt is Disallow: / for the whole hub. Changing the authored copy
	   itself is a CMS edit to site 66, shared with production, so it has been
	   left alone deliberately rather than overlooked.
	   ------------------------------------------------------------------ */
	function heading(sec, rows) {
		var h = sec.querySelector('#levels-heading');
		if (!h) { return; }
		var total = rows.length, done = 0, going = 0;
		rows.forEach(function (r) {
			if (r.done) { done++; }
			else if (r.seen) { going++; }
		});

		/* ONE TITLE, NOT FOUR. The per-state verbs ("Recommended to finish")
		   turned a shortlist into an instruction, and a heading that changes
		   wording between visits makes the section feel unstable - you cannot
		   learn where something is if its name keeps moving. The list below is
		   what is dynamic; its name should not be.

		   The one exception is worth keeping: once everything is read there is
		   nothing to recommend, and saying otherwise would be the last lie in
		   the section. */
		var txt = done === total ? 'Worth revisiting' : 'Recommended reading';
		if (h.textContent.trim() !== txt) { h.textContent = txt; }

		/* The key. Two variables ride on one ring and nothing on the page says
		   so; without this the colour ramp is decoration. Inserted after the
		   heading rather than before the grid so it reads as part of the
		   section title block, and only once. */
		var key = sec.querySelector('.sec-key');
		if (!key) {
			key = document.createElement('div');
			key.className = 'sec-key';
			h.parentNode.insertBefore(key, h.nextSibling);
		}
		var txt2 = '<i></i>';
		if (key.innerHTML !== txt2) { key.innerHTML = txt2; }
	}

	function paint() {
		var sec = document.querySelector(SEC);
		if (!sec) { return; }
		var map = load();
		var open = store('hub:open');
		var now = Date.now(), DAY = 86400000;

		/* Pass one: read the state. MEASURED is the important distinction -
		   a card can be opened with no depth recorded at all, either because
		   the reader bounced before the first scroll handler ran or because
		   they clicked it before depth tracking existed. Those cards used to
		   be floored to 8% and then CAPTIONED as "8% read", which invents a
		   measurement nobody took. The ring still shows a nub, because the
		   click was real; the words no longer claim a number. */
		var rows = Array.prototype.map.call(sec.querySelectorAll('.article-row'), function (row) {
			var href = norm(row.getAttribute('href'));
			var entry = map[href];
			var lvlEl = row.querySelector('.ar-path');
			return {
				el: row,
				href: href,
				done: row.classList.contains('is-read'),
				next: row.classList.contains('is-next'),
				measured: !!(entry && entry[0] > 0),
				depth: (entry && entry[0]) || 0,
				age: Math.max((entry && entry[1]) || 0, open[href] || 0),
				seen: !!open[href] || row.classList.contains('is-open') || !!(entry && entry[0] > 0),
				lvl: (+row.getAttribute('data-level')) || 1,
				words: lvlEl && lvlEl.textContent ? lvlEl.textContent.trim() : ''
			};
		});
		if (!rows.length) { return; }

		/* the rung this reader is actually working at - the same test the
		   ranking uses, so the captions and the order agree */
		var focus = 1;
		rows.forEach(function (r) {
			if (r.done || r.depth >= 0.3) { focus = Math.max(focus, r.lvl); }
		});

		rows.forEach(function (r) {
			var d = r.depth;
			if (r.done) { d = 1; }
			else {
				if (d > CEIL) { d = CEIL; }
				if (r.seen && d < FLOOR) { d = FLOOR; }
			}
			var pct = Math.round(d * 100);

			/* Two frames before the arc is set. The property is registered with an
			   initial-value of 0%, but setting the real value inside the same frame
			   as first paint gives the browser one computed style to work from and
			   no transition - the ring would simply appear at its final length. One
			   frame at 0% is what turns it into a sweep. */
			(function (el, v) {
				requestAnimationFrame(function () {
					requestAnimationFrame(function () { el.style.setProperty('--fill', v); });
				});
			})(r.el, pct + '%');

			/* ----------------------------------------------------------
			   THE CAPTION SAYS WHY THIS CARD IS WHERE IT IS.

			   An ordered list whose order is never explained is just a list
			   in a surprising sequence. Each card now gives the reason the
			   ranking moved it, in the ranking's own terms:

			     measured progress  the number, because it is the strongest
			                        reason and the one the ring is drawing
			     opened, no depth   say so plainly; do not invent a figure
			     left early         an old click that went nowhere, which is
			                        why it has sunk below untouched cards
			     level fit          for cards with no history, where this one
			                        sits relative to the rung you work at

			   The level words follow as context. They come from the card's
			   own .ar-path, which the template already carries and CSS hides,
			   so the caption costs no CMS content and cannot drift from the
			   colour - the ranking reads that same text to decide the level.

			   The suggested card adds nothing here: it already has the pill,
			   the border and the halo. A fourth marker saying the same thing
			   is how the Continue pill ended up being removed.
			   ---------------------------------------------------------- */
			var why;
			if (r.done) {
				why = 'Finished';
			} else if (r.measured) {
				why = '<b>' + pct + '%</b> read';
			} else if (r.seen) {
				var old = r.age && (now - r.age) / DAY > 14;
				why = old ? 'Opened a while back' : 'Opened, not read yet';
			} else if (r.lvl < focus) {
				why = 'Earlier step';
			} else if (r.lvl === focus) {
				why = 'At your level';
			} else if (r.lvl === focus + 1) {
				why = 'Your next step up';
			} else {
				why = 'Further ahead';
			}

			var body = r.el.querySelector('.ar-body');
			if (!body) { return; }
			var meta = body.querySelector('.ar-meta');
			if (!meta) {
				meta = document.createElement('span');
				meta.className = 'ar-meta';
				body.appendChild(meta);
			}
			/* only our own markup: pct is a rounded number, the words are escaped */
			var txt = r.words ? (why + ' &middot; ' + esc(r.words)) : why;
			if (meta.innerHTML !== txt) { meta.innerHTML = txt; }
		});

		heading(sec, rows);
	}

	function init() { record(); paint(); }
	if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
}


export function initHomeProgress() {
	markReadOnScroll();
	markReadOnClick();
	orderByReader();
	progressRing();
}
